import React, { useState, useEffect } from "react";
import "./NfcScan.css";
import { checkNfcSupport, readNfcChip } from "../../utils/nfcHelper";

function NfcScan({ onBack, onContinue, isSubmitting = false, personalInfo = {} }) {
  const [scanStatus, setScanStatus] = useState("idle"); // idle, scanning, reading, success, error
  const [scanProgress, setScanProgress] = useState(0); // 0 to 100%
  const [scanStageText, setScanStageText] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [scannedData, setScannedData] = useState(null);
  const [isNfcSupported, setIsNfcSupported] = useState(true);
  const [unsupportedReason, setUnsupportedReason] = useState("");

  useEffect(() => {
    const support = checkNfcSupport();
    setIsNfcSupported(support.supported);
    if (!support.supported) {
      setUnsupportedReason(support.reason || "Web NFC is only supported on NFC-enabled Android devices via Chrome browser.");
    }
  }, []);

  const handleStartScan = async () => {
    setErrorMessage(null);
    setScanStatus("scanning");
    setScanProgress(0);        // 0% — real data aye tab hi percentage badhegi
    setScanStageText("Hold your passport against the back of your phone...");

    const result = await readNfcChip((stageText, percent) => {
      setScanStageText(stageText);
      setScanProgress(percent);
      if (percent >= 70 && percent < 100) {
        setScanStatus("reading");
      }
    });

    if (result.success) {
      setScanStatus("success");
      setScanProgress(100);
      setScanStageText("✓ Chip 100% Verified");
      setScannedData(result.data);
    } else {
      setScanStatus("error");
      setScanProgress(0);
      setScanStageText("");
      setErrorMessage(result.error || "Failed to read NFC chip. Please try again.");
    }
  };

  const handleProceed = () => {
    if (scannedData) {
      onContinue(scannedData, null);
    }
  };

  return (
    <div className="nfc-container">
      <div className="nfc-card">
        <div>
          <div className="nfc-header">
            <button className="back-button" onClick={onBack} disabled={isSubmitting}>
              ←
            </button>
            <div className="header-progress">
              <div className="header-progress-fill" style={{ width: "66%" }}></div>
            </div>
          </div>

          <h2 className="nfc-title">Biometric NFC Scan</h2>
          <p className="nfc-subtitle">
            Hold your biometric passport or e-ID against the back of your phone to read the chip.
          </p>

          <div className="nfc-scan-body">
            {!isNfcSupported && (
              <div className="nfc-unsupported-banner">
                <svg className="unsupported-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                <p className="unsupported-text">{unsupportedReason}</p>
              </div>
            )}

            <div className={`nfc-illustration-box ${scanStatus}`}>
              <div className="nfc-pulse-ring ring-1"></div>
              <div className="nfc-pulse-ring ring-2"></div>
              <div className="nfc-pulse-ring ring-3"></div>

              <div className="nfc-phone-graphic">
                <svg viewBox="0 0 24 24" className="phone-icon" fill="none" stroke="currentColor">
                  <rect x="5" y="2" width="14" height="20" rx="3" strokeWidth="1.8" />
                  <line x1="10" y1="18" x2="14" y2="18" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="5" r="0.75" fill="currentColor" />
                </svg>

                <div className="nfc-waves-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 2a10 10 0 0 1 10 10" strokeWidth="2" strokeLinecap="round" />
                    <path d="M12 6a6 6 0 0 1 6 6" strokeWidth="2" strokeLinecap="round" />
                    <path d="M12 10a2 2 0 0 1 2 2" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>

                <div className="nfc-card-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <rect x="3" y="5" width="18" height="14" rx="2" strokeWidth="1.8" />
                    <circle cx="9" cy="11" r="2.5" strokeWidth="1.5" />
                    <line x1="14" y1="9" x2="19" y2="9" strokeWidth="1.5" strokeLinecap="round" />
                    <line x1="14" y1="13" x2="17" y2="13" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
              </div>

              <div className="nfc-status-pill">
                {scanStatus === "idle" && <span className="status-badge idle">Ready to scan</span>}
                {scanStatus === "scanning" && <span className="status-badge scanning">Hold passport near phone...</span>}
                {scanStatus === "reading" && <span className="status-badge reading">Reading chip data...</span>}
                {scanStatus === "success" && <span className="status-badge success">✓ Chip 100% Verified</span>}
                {scanStatus === "error" && <span className="status-badge error">Scan Failed</span>}
              </div>
            </div>

            {/* Live Scanning Progress Bar — sirf real chip event ke baad dikhega */}
            {(scanStatus === "scanning" || scanStatus === "reading" || scanStatus === "success") && scanProgress > 0 && (
              <div className="nfc-live-progress-container">
                <div className="nfc-progress-meta">
                  <span className="nfc-stage-label">
                    {scanStageText || "Waiting for chip..."}
                  </span>
                  <span className="nfc-percent-label">{scanProgress}%</span>
                </div>
                <div className="nfc-live-progress-track">
                  <div
                    className={`nfc-live-progress-bar ${scanStatus}`}
                    style={{ width: `${scanProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Waiting hint — jab scan shuru ho lekin chip touch nahi hoi abhi */}
            {scanStatus === "scanning" && scanProgress === 0 && scanStageText && (
              <p className="nfc-waiting-hint">{scanStageText}</p>
            )}

            {errorMessage && (
              <div className="nfc-error-message">
                <span>{errorMessage}</span>
              </div>
            )}

            {scanStatus === "success" && scannedData && (
              <div className="nfc-verified-summary">
                <div className="summary-field">
                  <span className="summary-title">Document Number</span>
                  <span className="summary-value">{scannedData.documentNumber || "Verified"}</span>
                </div>
                <div className="summary-field">
                  <span className="summary-title">Holder Name</span>
                  <span className="summary-value">{scannedData.fullName || personalInfo.fullName || "Verified"}</span>
                </div>
                {scannedData.expiryDate && (
                  <div className="summary-field">
                    <span className="summary-title">Expiry Date</span>
                    <span className="summary-value">{scannedData.expiryDate}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="nfc-footer-section">
          <div className="privacy-notice">
            <svg className="lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span>NFC encrypted transmission</span>
          </div>

          {scanStatus !== "success" ? (
            <button
              type="button"
              className="continue-button"
              onClick={handleStartScan}
              disabled={scanStatus === "scanning" || scanStatus === "reading" || isSubmitting || !isNfcSupported}
            >
              {scanStatus === "scanning" || scanStatus === "reading" ? (
                <>
                  <span className="btn-spinner"></span>
                  Scanning Chip...
                </>
              ) : (
                "Start NFC Scan"
              )}
            </button>
          ) : (
            <button
              type="button"
              className="continue-button"
              onClick={handleProceed}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner"></span>
                  Submitting...
                </>
              ) : (
                "Continue →"
              )}
            </button>
          )}

          <div className="document-footer">Secured by VerifiLite</div>
        </div>
      </div>
    </div>
  );
}

export default NfcScan;
